import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { IAggregatedCostInflow } from '../../../../../../models/aggregated-cost-inflow.model';
import { ICostCenterCostDistribution } from '../../../../../../models/cost-center-cost-distribution.model';
import { ICostInflow } from '../../../../../../models/cost-inflow.model';
import { ICostOutflow } from '../../../../../../models/cost-outflow.model';
import { ICostCenterCostHistoryDialogLayout } from '../../../../../../models/layout/cost-center-cost-history-dialog-layout.model';
import { ICostCenterCostRebookingState } from '../../../../../../services/facades/cost-accounting/cost-center-cost-rebooking-state.model';
import { CostCenterCostRebookingFacade } from '../../../../../../services/facades/cost-accounting/cost-center-cost-rebooking.facade';
import { RebookingLayoutService } from '../../../../../../services/layout/rebooking-layout.service';

@Component({
    templateUrl: './cost-center-cost-history-dialog.component.html',
    styleUrls: [ './cost-center-cost-history-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostCenterCostHistoryDialogComponent implements OnInit, OnDestroy {

    public rebookingState$: Observable<ICostCenterCostRebookingState>;
    public layout: ICostCenterCostHistoryDialogLayout;

    public treeControl: FlatTreeControl<IAggregatedCostInflow> = new FlatTreeControl<IAggregatedCostInflow>(node => node.level, node => node.expandable);

    public datasource: MatTreeFlatDataSource<IAggregatedCostInflow, ICostInflow> = new MatTreeFlatDataSource<IAggregatedCostInflow, ICostInflow>(this.treeControl, new MatTreeFlattener(
        (node: IAggregatedCostInflow, level: number): IAggregatedCostInflow => {
            const transformed = node;
            transformed.expandable = !!node.inflows && node.inflows.length > 0;
            transformed.level = level;
            return transformed;
        }, node => node.level,
        node => node.expandable, node => node.inflows));
    public transformedOutFlowsAggregated: IAggregatedCostInflow[] = [];
    public inflowsAggregated: IAggregatedCostInflow[] = [];
    public tabIndex: number = 0;

    private destroy$: Subject<void> = new Subject();

    constructor(private myRebookingFacade: CostCenterCostRebookingFacade,
                private myRebookingLayoutService: RebookingLayoutService,
                private myMatDialogRef: MatDialogRef<CostCenterCostHistoryDialogComponent>,
                @Inject(MAT_DIALOG_DATA) public data: ICostCenterCostDistribution) {
    }

    public ngOnInit(): void {
        this.layout = this.myRebookingLayoutService.getCostCenterCostHistoryDialog();
        this.rebookingState$ = this.myRebookingFacade.subState();
        this.tranformData();
        this.datasource.data = this.inflowsAggregated;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Remove Costs from memory
    }

    public close(): void {
        this.myMatDialogRef.close(false);
    }

    public onTabChange($event: MatTabChangeEvent): void {
        // Set the correct data for the table according to the active tab
        // And set the tabIndex-field so that the template can choose the correct columns
        this.tabIndex = $event.index;
        this.datasource.data = this.tabIndex === 0 ? this.inflowsAggregated : this.transformedOutFlowsAggregated;
    }

    private tranformData(): void {

        // Maps 1 outflow to n target-inflows

        this.rebookingState$.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(
                (prev, curr) =>
                    JSON.stringify(prev.inflowLookup) === JSON.stringify(curr.inflowLookup)
                    || JSON.stringify(prev.outflowLookup) === JSON.stringify(curr.outflowLookup),
            ),
        ).subscribe((s: ICostCenterCostRebookingState) => {
            const transformedOutflows = this.transformOutflowsToInflows(s.inflowLookup);
            this.transformedOutFlowsAggregated = this.aggregateInflows(transformedOutflows, s.outflowLookup, true);
            this.inflowsAggregated = this.aggregateInflows(this.data.apportionmentInflows, s.outflowLookup, false);
        });

    }

    private transformOutflowsToInflows(inflowLookup: Record<number, ICostInflow>): ICostInflow[] {
        const transformedOutflows: ICostInflow[] = [];

        for (const outflow of this.data.apportionmentOutflows) {
            for (const inflowId of outflow.apportionmentInflowIds) {
                if (!(inflowId in inflowLookup)) {
                    continue;
                }
                const inflow: ICostInflow = inflowLookup[ inflowId ];
                transformedOutflows.push(inflow);
            }
        }
        return transformedOutflows;
    }

    private aggregateInflows(inflows: ICostInflow[], outflowLookup: Record<number, ICostOutflow>, isTransformed: boolean): IAggregatedCostInflow[] {

        const map: Record<number, IAggregatedCostInflow> = {};

        for (const inflow of inflows) {
            const apportionmentId: number = isTransformed ? inflow.costApportionmentTargetId : outflowLookup[ inflow.costApportionmentOutflowSourceId ]?.costApportionmentSourceId;
            const agg: IAggregatedCostInflow = apportionmentId in map ? map[ apportionmentId ] : {
                inflows: [],
                valueSumCt: 0,
                costApportionmentId: apportionmentId,
            } as IAggregatedCostInflow;
            agg.valueSumCt += inflow.valueCt;
            agg.inflows.push(this.transformInflowToNode(inflow, apportionmentId));
            map[ apportionmentId ] = agg;
        }
        return Object.values(map);
    }

    private transformInflowToNode(inflow: ICostInflow, apportionmentId: number): IAggregatedCostInflow {
        return {
            valueCt: inflow.valueCt,
            costApportionmentId: apportionmentId,
            inflows: [],
            expandable: false,
            momentOfCalculation: inflow.momentOfCalculation,
        } as IAggregatedCostInflow;
    }

}
